<script setup lang="ts">
import {
    getApiGetRelatedAuctionsQueryKey,
    getApiGetRelatedDirectSalesQueryKey,
    SellingMethod,
    useApiGetRelatedAuctions,
    useApiGetRelatedDirectSales,
} from '~/apiClient';
import { type PlatformSaleItemEntity, type PlatformSaleListingItemEntity } from '~/types/sale.type';

// PROPS
interface Props {
    sale: PlatformSaleItemEntity;
}
const { sale } = defineProps<Props>();

type CarouselItemEntity =
    | {
          variant: 'skeleton';
      }
    | {
          variant: 'sale';
          sale: PlatformSaleListingItemEntity;
      };

// COMPOSABLES
const { tt } = useTypedI18n();
const { isLoggedIn } = useAuthUtils();
const [DefineTemplate, ReuseTemplate] = createReusableTemplate<{ items: CarouselItemEntity[] }>();
const apiParams = computed(() => {
    switch (sale.sellingMethod) {
        case SellingMethod.Auction:
            return { auctionId: sale.id };
        case SellingMethod.DirectSale:
            return { directSaleId: sale.id };
    }
});

const {
    data: relatedAuctions,
    isLoading: isLoadingAuctions,
    isEnabled: isEnabledAuctions,
    refetch: refetchAuctions,
} = useApiGetRelatedAuctions(apiParams, {
    query: { enabled: import.meta.client, queryKey: getApiGetRelatedAuctionsQueryKey(apiParams) },
});
const {
    data: relatedDirectSales,
    isLoading: isLoadingDirectSales,
    isEnabled: isEnabledDirectSales,
    refetch: refetchDirectSales,
} = useApiGetRelatedDirectSales(apiParams, {
    query: { enabled: import.meta.client, queryKey: getApiGetRelatedDirectSalesQueryKey(apiParams) },
});
const { setTrackingReferralListName, trackSelectSale } = useTracking();

// METHODS
const mapSale = (sale: PlatformSaleListingItemEntity): CarouselItemEntity => ({ variant: 'sale', sale });

// DATA
const auctions = computed<CarouselItemEntity[]>(() =>
    isLoadingAuctions.value || !isEnabledAuctions.value
        ? Array.from(Array(5)).map(() => ({ variant: 'skeleton' }))
        : (relatedAuctions.value?.auctions.map(mapSale) ?? [])
);
const directSales = computed<CarouselItemEntity[]>(() =>
    isLoadingDirectSales.value || !isEnabledDirectSales.value
        ? Array.from(Array(5)).map(() => ({ variant: 'skeleton' }))
        : (relatedDirectSales.value?.directSales.map(mapSale) ?? [])
);
const auctionCarouselTitle = computed(() =>
    sale.sellingMethod === SellingMethod.Auction ? tt('sdp.Auction.related.auctions') : tt('sdp.DirectSale.related.auctions')
);
const directSaleCarouselTitle = computed(() =>
    sale.sellingMethod === SellingMethod.Auction ? tt('sdp.Auction.related.directSales') : tt('sdp.DirectSale.related.directSales')
);
const ui = {
    container: 'gap-x-4 p-1',
    item: 'w-64 basis-auto',
    prev: 'disabled:hidden',
    next: 'disabled:hidden',
};

// HANDLERS
const onNavigate = (): void => {
    trackSelectSale();
    setTrackingReferralListName('related');
};

// WATCHERS
// The related sales are scoped to the viewer, so signing in or out changes what they should contain.
watch(isLoggedIn, () => {
    refetchAuctions();
    refetchDirectSales();
});
</script>

<template>
    <DefineTemplate v-slot="{ items }">
        <UCarousel v-slot="{ item }" :items="items" :ui="ui" arrows wheel-gestures>
            <SlpCardSkeleton v-if="item.variant === 'skeleton'" />
            <SlpCard v-else :sale="item.sale" @navigate="onNavigate" />
        </UCarousel>
    </DefineTemplate>
    <div class="flex flex-col gap-6" :class="{ 'flex-col-reverse': sale.sellingMethod === SellingMethod.DirectSale }">
        <div v-if="auctions.length" class="flex flex-col gap-6">
            <h3 class="text-highlighted text-2xl font-semibold md:text-3xl">{{ auctionCarouselTitle }}</h3>
            <ReuseTemplate :items="auctions" />
        </div>

        <div v-if="directSales.length" class="flex flex-col gap-6">
            <h3 class="text-highlighted text-2xl font-semibold md:text-3xl">{{ directSaleCarouselTitle }}</h3>
            <ReuseTemplate :items="directSales" />
        </div>
    </div>
</template>
